import {
    useLayoutEffect,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    Platform,
    View
} from "react-native";
import type INavigationMenuProps from "./type";
import type {
    NavigationMenuTriggerKeyConstantType,
    NavigationMenuPanelBehaviour,
    NavigationMenuItemMetaType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import NavigationMenuCollapsed from "./components/collapsed";
import NavigationMenuContent from "./components/content";
import NavigationMenuFeature from "./components/feature";
import NavigationMenuGroup from "./components/group";
import NavigationMenuItem from "./components/item";
import NavigationMenuLink from "./components/link";
import {
    NavigationMenuContext
} from "./context";
import {
    NAVIGATION_MENU_FULL_WIDTH_TRACKS,
    getNavigationMenuTrackCount,
    getNavigationMenuItemsMeta,
    getNavigationMenuContentId,
    focusNavigationMenuPanel,
    getNavigationMenuItems
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePointerType,
    useBreakpoint
} from "../../helpers/pointer";
import {
    getHorizontalStep
} from "../../helpers/direction";

const NavigationMenu: FC<INavigationMenuProps> = ({
    panelBehaviour = "floating",
    collapseBehaviour = "auto",
    openBehaviour = "hover",
    size = "medium",
    customLocalize,
    onValueChange,
    customTheme,
    children,
    footer,
    title,
    value,
    style
}) => {
    const {
        durations,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const breakpoint = useBreakpoint();

    const pointerType = usePointerType();

    const baseId = useId();

    const [
        internalValue,
        setInternalValue
    ] = useState<string | null>(null);

    const [
        isSheetOpen,
        setIsSheetOpen
    ] = useState(false);

    const triggerRefs = useRef(new Map<string, View | null>());

    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const focusRequestRef = useRef(false);

    const isPinnedRef = useRef(false);

    const surfaceRef = useRef<View>(null);

    const navRef = useRef<View>(null);

    const isControlled = value !== undefined;

    const activeValue = isControlled ? value : internalValue;

    const activeRef = useRef(activeValue);

    const isWeb = Platform.OS === "web";

    const isHoverEnabled = isWeb && pointerType === "fine" && openBehaviour === "hover";

    const isCollapsed = !isWeb || collapseBehaviour === "always" || (collapseBehaviour === "auto" && breakpoint === "compact");

    const items = getNavigationMenuItems(children);

    const itemsMeta = getNavigationMenuItemsMeta(items);

    const maxTracks = Math.max(0, ...items.map((item) => getNavigationMenuTrackCount(item.props.children)));

    const currentPanelBehaviour: NavigationMenuPanelBehaviour = panelBehaviour === "fullWidth" || maxTracks >= NAVIGATION_MENU_FULL_WIDTH_TRACKS ? "fullWidth" : "floating";

    const {
        list: listDynamicStyle
    } = useStyles({
        spaces
    });

    const handleClearOpenTimer = useCallback(() => {
        if(openTimerRef.current === null) {
            return;
        }

        clearTimeout(openTimerRef.current);

        openTimerRef.current = null;
    }, []);

    const handleCancelClose = useCallback(() => {
        if(closeTimerRef.current === null) {
            return;
        }

        clearTimeout(closeTimerRef.current);

        closeTimerRef.current = null;
    }, []);

    const handleChange = useCallback((nextValue: string | null) => {
        if(activeRef.current === nextValue) {
            return;
        }

        if(!isControlled) {
            activeRef.current = nextValue;

            setInternalValue(nextValue);
        }

        onValueChange?.(nextValue);
    }, [
        isControlled,
        onValueChange
    ]);

    const handleClose = useCallback(() => {
        handleClearOpenTimer();

        handleCancelClose();

        isPinnedRef.current = false;

        handleChange(null);
    }, [
        handleClearOpenTimer,
        handleCancelClose,
        handleChange
    ]);

    const handleOpen = useCallback((itemKey: string, isPinned: boolean) => {
        handleClearOpenTimer();

        handleCancelClose();

        if(activeRef.current === null) {
            isPinnedRef.current = false;
        }

        if(isPinned) {
            isPinnedRef.current = true;
        }

        handleChange(itemKey);
    }, [
        handleClearOpenTimer,
        handleCancelClose,
        handleChange
    ]);

    const handleScheduleOpen = useCallback((itemKey: string) => {
        if(!isHoverEnabled) {
            return;
        }

        handleCancelClose();

        if(activeRef.current === itemKey) {
            handleClearOpenTimer();

            return;
        }

        handleClearOpenTimer();

        if(activeRef.current !== null) {
            handleOpen(itemKey, false);

            return;
        }

        openTimerRef.current = setTimeout(() => {
            handleOpen(itemKey, false);
        }, durations.hoverIntent);
    }, [
        durations.hoverIntent,
        handleClearOpenTimer,
        handleCancelClose,
        isHoverEnabled,
        handleOpen
    ]);

    const handleScheduleClose = useCallback(() => {
        if(!isHoverEnabled) {
            return;
        }

        handleClearOpenTimer();

        if(isPinnedRef.current || activeRef.current === null || closeTimerRef.current !== null) {
            return;
        }

        closeTimerRef.current = setTimeout(handleClose, durations.hoverClose);
    }, [
        durations.hoverClose,
        handleClearOpenTimer,
        isHoverEnabled,
        handleClose
    ]);

    const handlePress = useCallback((itemKey: string) => {
        if(activeRef.current !== itemKey) {
            handleOpen(itemKey, true);

            return;
        }

        if(isPinnedRef.current) {
            handleClose();

            return;
        }

        handleClearOpenTimer();

        handleCancelClose();

        isPinnedRef.current = true;
    }, [
        handleClearOpenTimer,
        handleCancelClose,
        handleClose,
        handleOpen
    ]);

    const handleSheetClose = useCallback(() => {
        setIsSheetOpen(false);
    }, []);

    const handleSheetOpen = useCallback(() => {
        setIsSheetOpen(true);
    }, []);

    const handleLinkPress = useCallback(() => {
        handleClose();

        handleSheetClose();
    }, [
        handleSheetClose,
        handleClose
    ]);

    const getContentId = useCallback((itemKey: string) => getNavigationMenuContentId({
        itemKey,
        baseId
    }), [
        baseId
    ]);

    const handleFocusItem = (itemMeta: NavigationMenuItemMetaType) => {
        triggerRefs.current.get(itemMeta.key)?.focus();

        if(activeRef.current === null) {
            return;
        }

        if(itemMeta.hasContent) {
            handleOpen(itemMeta.key, true);

            return;
        }

        handleClose();
    };

    const getEnabledItem = (startIndex: number, step: 1 | -1): NavigationMenuItemMetaType | undefined => {
        for(let index = startIndex; index >= 0 && index < itemsMeta.length; index += step) {
            const itemMeta = itemsMeta[index];

            if(itemMeta && !itemMeta.isDisabled) {
                return itemMeta;
            }
        }

        return undefined;
    };

    const handleTriggerKeyDown = ({
        itemKey,
        event
    }: NavigationMenuTriggerKeyConstantType) => {
        const index = itemsMeta.findIndex((itemMeta) => itemMeta.key === itemKey);

        const currentItem = itemsMeta[index];

        if(!currentItem) {
            return;
        }

        if(event.key === "Tab" && !event.shiftKey) {
            if(activeRef.current === itemKey && currentItem.hasContent && focusNavigationMenuPanel({
                surfaceRef
            })) {
                event.preventDefault();
            }

            return;
        }

        if(event.key === "ArrowDown" && currentItem.hasContent && !currentItem.isDisabled) {
            event.preventDefault();

            if(activeRef.current === itemKey && focusNavigationMenuPanel({
                surfaceRef
            })) {
                return;
            }

            focusRequestRef.current = true;

            handleOpen(itemKey, true);

            return;
        }

        const step = getHorizontalStep({
            key: event.key,
            direction
        });

        const nextItem = (() => {
            if(step !== 0) {
                return getEnabledItem(index + step, step);
            }

            if(event.key === "Home") {
                return getEnabledItem(0, 1);
            }

            if(event.key === "End") {
                return getEnabledItem(itemsMeta.length - 1, -1);
            }

            return undefined;
        })();

        if(!nextItem) {
            return;
        }

        event.preventDefault();

        handleFocusItem(nextItem);
    };

    useLayoutEffect(() => {
        activeRef.current = activeValue;
    }, [
        activeValue
    ]);

    useEffect(() => {
        if(isCollapsed) {
            handleClose();

            return;
        }

        handleSheetClose();
    }, [
        isCollapsed
    ]);

    useEffect(() => {
        return () => {
            handleClearOpenTimer();

            handleCancelClose();
        };
    }, [
        handleClearOpenTimer,
        handleCancelClose
    ]);

    const context = useMemo(() => ({
        panelBehaviour: currentPanelBehaviour,
        handleTriggerKeyDown,
        handleScheduleClose,
        handleScheduleOpen,
        handleCancelClose,
        focusRequestRef,
        handleLinkPress,
        customLocalize,
        getContentId,
        activeValue,
        customTheme,
        handleClose,
        handlePress,
        triggerRefs,
        surfaceRef,
        navRef,
        size
    }), [
        currentPanelBehaviour,
        handleScheduleOpen,
        handleScheduleClose,
        handleCancelClose,
        handleLinkPress,
        customLocalize,
        getContentId,
        activeValue,
        handlePress,
        handleClose,
        customTheme,
        itemsMeta.map((itemMeta) => `${itemMeta.key}:${itemMeta.isDisabled}:${itemMeta.hasContent}`).join("|"),
        direction,
        size
    ]);

    const renderBody = () => {
        if(isCollapsed) {
            return <NavigationMenuCollapsed
                handleSheetClose={handleSheetClose}
                handleSheetOpen={handleSheetOpen}
                isSheetOpen={isSheetOpen}
                footer={footer}
                items={items}
                style={style}
                title={title}
            />;
        }

        return <View
            style={[
                style,
                stylesheet.root
            ]}
            aria-label={title}
            role="navigation"
            ref={navRef}
        >
            <View
                style={[
                    stylesheet.list,
                    listDynamicStyle
                ]}
                role="list"
            >
                {items}
            </View>
        </View>;
    };

    return <NavigationMenuContext.Provider
        value={context}
    >
        {renderBody()}
    </NavigationMenuContext.Provider>;
};
export default Object.assign(NavigationMenu, {
    Content: NavigationMenuContent,
    Feature: NavigationMenuFeature,
    Group: NavigationMenuGroup,
    Item: NavigationMenuItem,
    Link: NavigationMenuLink
});
